import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Calendar, ArrowRight, Search, Tag, Mail, Loader2, CheckCircle2 } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import PageHero from '../components/layout/PageHero';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const categories = ["Alle", "Bewertung", "Verkauf", "Kauf", "Nachfolge", "M&A"];

// Static blog posts (fallback)
const staticPosts = [
  {
    id: "static-1",
    title: "Unternehmensbewertung: Ein Leitfaden für KMU",
    excerpt: "Alles was Sie über die Bewertung Ihres Unternehmens wissen müssen.",
    created_at: "2025-07-10",
    slug: "unternehmensbewertung-leitfaden-kmu",
    category: "Bewertung",
    is_static: true
  },
  {
    id: "static-2",
    title: "Firma verkaufen: Der optimale Zeitpunkt",
    excerpt: "Wann ist der beste Moment, um Ihr Unternehmen zu verkaufen?",
    created_at: "2025-07-05",
    slug: "firma-verkaufen-optimaler-zeitpunkt",
    category: "Verkauf",
    is_static: true
  },
  {
    id: "static-3",
    title: "Due Diligence beim Unternehmenskauf",
    excerpt: "Worauf Käufer bei der Prüfung achten sollten.",
    created_at: "2025-06-28",
    slug: "due-diligence-unternehmenskauf",
    category: "Kauf",
    is_static: true
  }
];

const BlogPage = () => {
  const [activeCategory, setActiveCategory] = useState("Alle");
  const [searchQuery, setSearchQuery] = useState("");
  const [dynamicPosts, setDynamicPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Newsletter state
  const [newsletterForm, setNewsletterForm] = useState({ first_name: '', last_name: '', email: '' });
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  // Fetch published articles from API
  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const response = await fetch(`${API_URL}/api/blog/articles?published_only=true`);
        if (response.ok) {
          const data = await response.json();
          setDynamicPosts(data.articles || []);
        }
      } catch {
        // Error fetching articles - silent fail, show static content
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, []);

  // Newsletter subscription handler
  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    
    if (!newsletterForm.first_name || !newsletterForm.last_name || !newsletterForm.email) {
      toast.error('Bitte füllen Sie alle Felder aus');
      return;
    }
    
    setIsSubscribing(true);
    try {
      const response = await fetch(`${API_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newsletterForm)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSubscribed(true);
        setNewsletterForm({ first_name: '', last_name: '', email: '' });
        toast.success(data.message || 'Erfolgreich angemeldet!');
      } else {
        throw new Error(data.detail || 'Fehler bei der Anmeldung');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsSubscribing(false);
    }
  };

  // Combine static and dynamic posts
  const allPosts = [...dynamicPosts, ...staticPosts];

  const filteredPosts = allPosts.filter(post => {
    const matchesCategory = activeCategory === "Alle" || post.category === activeCategory;
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          (post.excerpt || post.meta_description || '').toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <Helmet>
        <title>Ratgeber: Firmenverkauf, Nachfolge & Bewertung | KMU Schweiz</title>
        <meta name="description" content="Fachartikel für Schweizer KMU-Inhaber zu Firmenverkauf, Nachfolgeregelung, Firmenkauf und M&A – mit Rechenbeispielen, Checklisten und Methoden." />
        <link rel="canonical" href="https://www.unternehmenbewerten.ch/blog" />
      </Helmet>
      
      <main className="pt-20">
        <PageHero
          title="Ratgeber für Firmenverkauf, Nachfolge und Bewertung"
          subtitle="Fachartikel für Schweizer KMU-Inhaber – mit Rechenbeispielen, Checklisten und bewährten Methoden."
          breadcrumb="Ratgeber"
        />

        {/* Filters */}
        <section className="py-8 bg-white border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col lg:flex-row gap-6 items-start lg:items-center justify-between">
              {/* Categories */}
              <div className="flex flex-wrap gap-2">
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => setActiveCategory(category)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      activeCategory === category
                        ? 'bg-[#FF0000] text-white'
                        : 'bg-[#f8f9fa] text-[#5a5d5b] hover:text-[#FF0000]'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>

              {/* Search */}
              <div className="relative w-full lg:w-auto">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#888b89]" />
                <input
                  type="text"
                  placeholder="Artikel suchen..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full lg:w-80 pl-12 pr-4 py-3 bg-[#f8f9fa] border border-gray-200 rounded-xl text-[#1a1c1b] placeholder-[#888b89] focus:outline-none focus:border-[#FF0000] transition-colors"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Blog Grid */}
        <section className="py-12 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {loading ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3].map(i => (
                  <div key={i} className="animate-pulse bg-gray-100 rounded-2xl h-80" />
                ))}
              </div>
            ) : filteredPosts.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredPosts.map((post) => (
                  <Link
                    key={post.id}
                    to={post.is_static ? '#' : `/blog/${post.slug}`}
                    className={`group bg-white rounded-2xl border border-gray-100 overflow-hidden hover:border-[#FF0000]/30 hover:shadow-lg transition-all duration-300 ${
                      post.is_static ? 'opacity-60 cursor-not-allowed' : ''
                    }`}
                    onClick={e => post.is_static && e.preventDefault()}
                  >
                    {/* Image */}
                    <div className="aspect-video bg-[#FFE5E5] relative overflow-hidden">
                      {post.image_url ? (
                        <img 
                          src={post.image_url} 
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 relative z-10"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div 
                        className="absolute inset-0 bg-gradient-to-br from-[#FF0000]/10 to-transparent flex items-center justify-center"
                        style={{ display: post.image_url ? 'none' : 'flex' }}
                      >
                        <span className="text-[#FF0000]/30 text-6xl font-bold">B</span>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-6">
                      {/* Category & Date */}
                      <div className="flex items-center justify-between mb-4">
                        <span className="px-3 py-1 rounded-full bg-[#FFE5E5] text-[#CC0000] text-xs font-medium flex items-center gap-1">
                          <Tag size={12} />
                          {post.category}
                        </span>
                        <div className="flex items-center gap-2 text-[#888b89] text-sm">
                          <Calendar size={14} />
                          <span>{new Date(post.created_at).toLocaleDateString('de-CH')}</span>
                        </div>
                      </div>

                      {/* Title */}
                      <h3 className="text-[#1a1c1b] font-semibold text-lg mb-3 group-hover:text-[#FF0000] transition-colors">
                        {post.title}
                      </h3>

                      {/* Excerpt */}
                      <p className="text-[#5a5d5b] text-sm leading-relaxed mb-4 line-clamp-2">
                        {post.excerpt || post.meta_description}
                      </p>

                      {/* Read more */}
                      {!post.is_static && (
                        <div className="flex items-center gap-2 text-[#FF0000] font-medium text-sm">
                          <span>Weiterlesen</span>
                          <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                        </div>
                      )}
                      
                      {post.is_static && (
                        <div className="text-xs text-[#888b89]">
                          Demnächst verfügbar
                        </div>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <p className="text-[#5a5d5b] text-lg">Keine Artikel gefunden.</p>
              </div>
            )}
          </div>
        </section>

        {/* Newsletter CTA */}
        <section className="py-24 bg-[#f4f5f3]">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <Mail className="mx-auto text-[#FF0000] mb-4" size={40} />
            <h2 className="heading-2 mb-6">
              <span className="text-[#FF0000]">Newsletter</span> abonnieren
            </h2>
            <p className="body-medium mb-8">
              Erhalten Sie die neuesten Artikel und Tipps direkt in Ihr Postfach.
            </p>
            
            {subscribed ? (
              <div className="flex items-center justify-center gap-3 text-green-600 bg-green-50 p-6 rounded-2xl">
                <CheckCircle2 size={24} />
                <span className="font-medium">Vielen Dank für Ihre Anmeldung!</span>
              </div>
            ) : (
              <form onSubmit={handleNewsletterSubmit} className="space-y-4 max-w-md mx-auto">
                <div className="grid grid-cols-2 gap-4">
                  <input
                    type="text"
                    placeholder="Vorname *"
                    value={newsletterForm.first_name}
                    onChange={(e) => setNewsletterForm({ ...newsletterForm, first_name: e.target.value })}
                    className="px-4 py-3 bg-white border border-gray-200 rounded-xl text-[#1a1c1b] placeholder-[#888b89] focus:outline-none focus:border-[#FF0000] transition-colors"
                    data-testid="newsletter-firstname"
                    required
                  />
                  <input
                    type="text"
                    placeholder="Nachname *"
                    value={newsletterForm.last_name}
                    onChange={(e) => setNewsletterForm({ ...newsletterForm, last_name: e.target.value })}
                    className="px-4 py-3 bg-white border border-gray-200 rounded-xl text-[#1a1c1b] placeholder-[#888b89] focus:outline-none focus:border-[#FF0000] transition-colors"
                    data-testid="newsletter-lastname"
                    required
                  />
                </div>
                <input
                  type="email"
                  placeholder="E-Mail-Adresse *"
                  value={newsletterForm.email}
                  onChange={(e) => setNewsletterForm({ ...newsletterForm, email: e.target.value })}
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-[#1a1c1b] placeholder-[#888b89] focus:outline-none focus:border-[#FF0000] transition-colors"
                  data-testid="newsletter-email"
                  required
                />
                <Button
                  type="submit"
                  disabled={isSubscribing}
                  className="w-full bg-[#FF0000] hover:bg-[#CC0000] text-white py-3"
                  data-testid="newsletter-submit"
                >
                  {isSubscribing ? (
                    <>
                      <Loader2 size={18} className="mr-2 animate-spin" />
                      Wird angemeldet...
                    </>
                  ) : (
                    'Newsletter abonnieren'
                  )}
                </Button>
                <p className="text-xs text-[#888b89]">
                  Mit der Anmeldung akzeptieren Sie unsere{' '}
                  <Link to="/datenschutz" className="text-[#FF0000] hover:underline">
                    Datenschutzerklärung
                  </Link>
                  .
                </p>
              </form>
            )}
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default BlogPage;
